import type { InvestmentStatus } from "@eu.mdotm.api/sphere";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { AgGridWithModules, useAgGridSelectedNodes } from "$root/third-party-integrations/ag-grid/ag-grid";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { sphereColumnsDef } from "$root/ui-lib/table/column/platform-columns-definition";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { DebouncedSearchInput, Dialog, DialogFooter, type DialogProps } from "@mdotm/shadcn/legacy/components";
import { Button, Pagination, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { useQuery } from "@tanstack/react-query";
import type { ColDef } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { useState } from "react";

export type InvestmentPickerDialogProvider = {
	uuid: string;
	name: string;
	status: InvestmentStatus;
};

export type InvestmentPickerDialogProps = {
	title: string;
	show: boolean;
	onClose(): void;
	onSubmitAsync(entities: InvestmentPickerDialogProvider[]): Promise<void>;
	multiple?: boolean;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
} & (
	| {
			compositionProvider(search?: string): Promise<InvestmentPickerDialogProvider[]>;
			pagination?: never;
	  }
	| {
			compositionProvider?: never;
			pagination: {
				pageSize: number;
				page: number;
				compositionPageProvider(params: {
					page: number;
					search: string;
				}): Promise<{ items: InvestmentPickerDialogProvider[]; total: number }>;
			};
	  }
);

type RowsData = { rows: InvestmentPickerDialogProvider[]; total: number };

const columnDefs: ColDef<InvestmentPickerDialogProvider>[] = [
	sphereColumnsDef.investmentName<InvestmentPickerDialogProvider>(undefined, { editable: false, flex: 1 }, true),
	sphereColumnsDef.entityStatus<InvestmentPickerDialogProvider>("status", { editable: false, width: 150 }),
];

export function InvestmentPickerDialog({
	title,
	show,
	onClose,
	onSubmitAsync,
	multiple,
	onAnimationStateChange,
	...rest
}: InvestmentPickerDialogProps): React.JSX.Element {
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [page, setPage] = useState(rest.pagination?.page ?? 1);
	const [gridRef, setGridRef] = useState<AgGridReact<InvestmentPickerDialogProvider> | null>(null);

	const rawQuery = useQuery({
		queryKey: rest.pagination
			? (["investment-picker-dialog", "paged", debouncedSearch, page] as const)
			: (["investment-picker-dialog", "list", debouncedSearch] as const),
		queryFn: (): Promise<
			InvestmentPickerDialogProvider[] | { items: InvestmentPickerDialogProvider[]; total: number }
		> =>
			rest.pagination
				? rest.pagination.compositionPageProvider({ page, search: debouncedSearch })
				: rest.compositionProvider(debouncedSearch),
		// Keep the previous rows visible while a new search/page loads so the grid never unmounts
		// (the loading fallback would otherwise replace it). This keeps AG Grid mounted and only
		// delta-updates rowData, so the columns keep the sizes they got on the initial render
		// instead of re-initializing (and visibly expanding) on every search or data load.
		keepPreviousData: true,
	});

	const rowsQuery = useMappedQuery(
		(data): RowsData => (Array.isArray(data) ? { rows: data, total: 0 } : { rows: data.items, total: data.total }),
		[],
		rawQuery,
	);

	const selectedEntities = useAgGridSelectedNodes(gridRef);

	async function handleProceed(): Promise<void> {
		await onSubmitAsync(selectedEntities);
		onClose();
	}

	return (
		<Dialog
			size="xxlarge"
			show={show}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			header={title}
			onSubmitAsync={handleProceed}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton
							palette="primary"
							disabled={selectedEntities.length === 0}
							data-qualifier="PortfolioLab/Picker/Proceed"
						>
							Proceed
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							Cancel
						</Button>
					}
				/>
			}
		>
			<DebouncedSearchInput
				externalQuery=""
				placeholder="Search..."
				className="mb-4"
				data-qualifier="PortfolioLab/Picker/Search"
				onChange={(q) => {
					setDebouncedSearch(q);
					setPage(1);
				}}
			/>
			<ReactQueryWrapperBase query={rowsQuery}>
				{({ rows, total }) => (
					<>
						<div className="flex-1 min-h-0" style={{ height: 400 }}>
							<AgGridWithModules<InvestmentPickerDialogProvider>
								{...agExtra.checkboxColumn({
									mode: multiple ? "multiRow" : "singleRow",
									selectAll: "all",
									// enableClickSelection: true,
								})}
								rowData={rows}
								columnDefs={columnDefs}
								onGridReady={(e) => setGridRef({ api: e.api } as AgGridReact<InvestmentPickerDialogProvider>)}
								getRowId={(p) => p.data.uuid}
							/>
						</div>
						{rest.pagination && (
							<div className="mt-2">
								<Pagination currentPage={page} totalResults={total} onPageChange={(p) => setPage(p)} />
							</div>
						)}
					</>
				)}
			</ReactQueryWrapperBase>
		</Dialog>
	);
}
